import { IonPage } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useGetUnitInactiveById, useGetUnitInactiveBySerialImei } from '@apps-common/apis/portal/unit'
import { UnitActivationCustomModifyView } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/custom-activation'
import { UnitActivationReplaceModifyView } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/replace-activation'
import { UnitActivationTemplateModifyView } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/template-activation'
import { TemplateModify } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/template-modify'
import { PageTitle, SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm } from '@apps-common/components/elements'
import { Header, LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn, motionGrowFadeIn } from '@apps-common/config/motion-variants'
import { GarbageProvider } from '@apps-common/contexts/GarbageContext'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { IActivationServiceRequestTemplateModel } from '@apps-common/types/portal/service-request'
import { ROUTE_CREATE_UNIT_ACTIVATION, ROUTE_EDIT_UNIT_ACTIVATION } from '@apps-radionet-portal-root/src/config/private-routes'

export enum ModifyUnitActivationStep {
  companies = 'companies',
  unit = 'unit',
  plan = 'plan',
  confirm = 'confirm',
  submit = 'submit'
}

export type stepDirection = 'forward' | 'backward'

export const UnitActivationModifyPage = () => {
  const { activationId: aId } = useParams<{ activationId: string }>()
  const { navigateReplace } = useNavigation()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)

  const unitId = queryParams.get('id')
  const unitSerialNo = queryParams.get('serialNo')
  const unitImei = queryParams.get('imei')

  const activationId = parseInt(aId, 10)
  const isEditing = !Number.isNaN(activationId)

  const [activationType, setActivationType] = useState<'custom' | 'replacement' | 'template'>()
  const [template, setTemplate] = useState<IActivationServiceRequestTemplateModel>()

  const isUsingDefaultUnit = !!unitId || (!!unitSerialNo && !!unitImei)

  const defaultUsingId = isUsingDefaultUnit && !!unitId
  const defaultUsingSerialImei = isUsingDefaultUnit && !unitId && !!unitSerialNo && !!unitImei

  const {
    data: unitInactiveById,
    isLoading: unitInactiveByIdIsLoading,
    isFetching: unitInactiveByIdIsFetching,
    isError: unitInactiveByIdIsError,
    refetch: refetchUnitInactiveById
  } = useGetUnitInactiveById({
    config: { retry: false, enabled: defaultUsingId },
    id: unitId || ''
  })

  const {
    data: unitInactiveBySerialImei,
    isLoading: unitInactiveBySerialImeiIsLoading,
    isFetching: unitInactiveBySerialImeiIsFetching,
    isError: unitInactiveBySerialImeiIsError,
    refetch: refetchUnitInactiveBySerialImei
  } = useGetUnitInactiveBySerialImei({
    config: { retry: false, enabled: defaultUsingSerialImei },
    serialNo: unitSerialNo || '',
    imei: unitImei || ''
  })

  const defaultUnitLoading =
    unitInactiveByIdIsLoading || unitInactiveByIdIsFetching || unitInactiveBySerialImeiIsLoading || unitInactiveBySerialImeiIsFetching

  const defaultUnitIsError = (defaultUsingId && unitInactiveByIdIsError) || (defaultUsingSerialImei && unitInactiveBySerialImeiIsError)

  useEffect(() => {
    if (isEditing) {
      setActivationType('custom')
    }
  }, [isEditing])

  useEffect(() => {
    if (template) {
      setActivationType('template')
    }
  }, [template])

  return (
    <IonPage>
      <Header>
        <Navbar showBack transparent />
        <Breadcrumbs route={activationId ? ROUTE_EDIT_UNIT_ACTIVATION : ROUTE_CREATE_UNIT_ACTIVATION} />
        <div className='flex items-center justify-between md:justify-start gap-[20px] pb-[5px]'>
          <PageTitle classes='capitalize flex items-center justify-start gap-[8px]'>
            Activation{' '}
            {!!activationId && !Number.isNaN(activationId) && <div className='text-[0.6em] leading-[0.8em] !jost-medium'>(Editing)</div>}
            {!activationId && !!activationType && <div className='text-[0.6em] mt-1 jost-medium'>({activationType})</div>}
          </PageTitle>
          <AnimatePresence>
            {!isEditing && activationType && (
              <ModalButton
                component={ConfirmModal}
                disabled={false}
                classes=''
                componentProps={{
                  text: 'Reset',
                  description: `Confirm you would like to reset this activation and return to activation type selection?
                  
                  Any changes will be lost.`,
                  action: ConfirmModalAction.Warning
                }}
                tooltip={{
                  id: 'reset-activation-tooltip',
                  message: 'Clear and restart activation'
                }}
                confirmCallback={() => {
                  setActivationType(undefined)
                  setTemplate(undefined)
                }}
              >
                <ButtonRoundedSm
                  type='div'
                  loading={false}
                  text='Reset selection'
                  icon={<commonIcons.arrowRight className='rotate-180' />}
                  className='action-brand px-3 mt-1 !min-w-0 hidden md:flex'
                />
                <ButtonRoundedSm
                  type='div'
                  loading={false}
                  text='Reset'
                  icon={<commonIcons.arrowRight className='rotate-180' />}
                  className='action-brand px-3 mt-1 !min-w-0 flex md:hidden'
                />
              </ModalButton>
            )}
          </AnimatePresence>
        </div>
      </Header>
      <LayoutContainer>
        <LayoutPadding topPadding='pt-[20px]'>
          <GarbageProvider>
            <AnimatePresence>
              {defaultUnitIsError && (
                <motion.div {...motionGrowFadeIn(0.25)} className='sm:flex items-center justify-start mb-[25px] max-w-[400px]'>
                  <div className='rounded-2xl px-[20px] py-[20px] bg-warning/50 ring-1 ring-warning text-text'>
                    <div className='flex items-center justify-center gap-[15px]'>
                      <div className='flex flex-col gap-[15px]'>
                        <div>Default device failed to load</div>
                        <div className='text-[0.8em]'>
                          The device passed into this page failed to load. This could be that is has already been activated or does not
                          exist.
                        </div>
                        <div className='flex items-center justify-center gap-[10px] mt-[10px]'>
                          <ButtonRoundedSm
                            type='button'
                            loading={defaultUnitLoading}
                            text='Retry'
                            icon={<commonIcons.reload />}
                            className='action-success'
                            callback={() => {
                              if (defaultUsingId) {
                                refetchUnitInactiveById()
                              } else if (defaultUsingSerialImei) {
                                refetchUnitInactiveBySerialImei()
                              }
                            }}
                          />
                          <ButtonRoundedSm
                            type='button'
                            loading={defaultUnitLoading}
                            text='Cancel'
                            icon={<commonIcons.close className='text-[1.1rem]' />}
                            className='action-neutral'
                            callback={() =>
                              navigateReplace({
                                routeUrl: ROUTE_CREATE_UNIT_ACTIVATION.buildPath(),
                                route: ROUTE_CREATE_UNIT_ACTIVATION
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence mode='wait'>
              {!activationType && (
                <motion.div
                  key='activation-type-selection'
                  className='pt-3 flex justify-center md:justify-start max-w-[600px]'
                  {...motionFadeIn(0.2)}
                >
                  <div className='flex flex-col gap-3 text-text'>
                    <div className='md:text-[1.2em] pb-2'>Which type of activation would you like to create?</div>
                    <div className='grid md:grid-cols-2 gap-6'>
                      <div>
                        <div className='bg-surface border border-border flex flex-col items-start justify-start gap-6 p-6 rounded-3xl w-full md:min-w-[300px]'>
                          <SectionTitle text='Fixed' />
                          <div className='w-full text-center flex flex-col items-center justify-center gap-2'>
                            <button type='button' className='action-brand w-full p-2' onClick={() => setActivationType('custom')}>
                              <div className='max-w-[180px]'>
                                <div className='jost-medium'>Custom</div>
                              </div>
                            </button>
                            <div className='text-[0.8rem] text-text-muted/70'>Activate a new device, selecting all options.</div>
                          </div>

                          <div className='w-full text-center flex flex-col items-center justify-center gap-2'>
                            <button type='button' className='action-brand w-full p-2' onClick={() => setActivationType('replacement')}>
                              <div className='max-w-[180px]'>
                                <div className='jost-medium'>Replacement</div>
                              </div>
                            </button>
                            <div className='text-[0.8rem] text-text-muted/70'>Activate a new device, replacing an existing.</div>
                          </div>
                        </div>
                      </div>
                      <TemplateModify onSelected={(data: IActivationServiceRequestTemplateModel) => setTemplate(data)} />
                    </div>
                  </div>
                </motion.div>
              )}

              {activationType === 'custom' && (
                <div key='activation-type-custom-view' {...motionFadeIn(0.2, 2)}>
                  <UnitActivationCustomModifyView
                    unitActivationId={activationId}
                    defaultUnit={unitInactiveById || unitInactiveBySerialImei}
                  />
                </div>
              )}
              {activationType === 'replacement' && (
                <div key='activation-type-replacement-view' {...motionFadeIn(0.2, 2)}>
                  <UnitActivationReplaceModifyView
                    unitActivationId={activationId}
                    defaultUnit={unitInactiveById || unitInactiveBySerialImei}
                  />
                </div>
              )}
              {activationType === 'template' && template && (
                <div key='activation-type-template-view' {...motionFadeIn(0.2, 2)}>
                  <UnitActivationTemplateModifyView
                    unitActivationId={activationId}
                    defaultUnit={unitInactiveById || unitInactiveBySerialImei}
                    template={template}
                  />
                </div>
              )}
            </AnimatePresence>
          </GarbageProvider>
        </LayoutPadding>
      </LayoutContainer>
    </IonPage>
  )
}
